/* eslint-disable no-unused-vars */
import { Fragment, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import * as ga from "@/lib/analytics";

const Logo = dynamic(
  () => import("@/components/Logo").then((res) => res.Logo),
  { ssr: true }
);

const footerColumns = [
  {
    title: "Navigate",
    links: [
      { label: "How It Works", href: "/#how-it-works" },
      { label: "Why Ledgerowl", href: "/#services" },
      { label: "Pricing", href: "/pricing" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "News", href: "/news" },
      { label: "Contact Us", href: "/contact" },
      { label: "Ledgerowl vs Xero", href: "/vs-xero" },
      { label: "Ledgerowl vs Outsourcing", href: "/vs-outsourcing" },
    ],
  },
];

const SocialLinks = () => (
  <div className="flex gap-4">
    <Link
      href="https://www.linkedin.com/company/ledgerowl/mycompany/"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-cool-500 hover:text-blue-primary-500 transition-colors"
    >
      <Image
        src="https://res.cloudinary.com/dsi05tdxx/image/upload/v1675733168/ledgerowl-site/helper/newlinkedinicon_uoctyq.svg"
        unoptimized
        width={18}
        height={18}
        alt="LinkedIn"
      />
    </Link>
    <Link
      href="https://www.instagram.com/ledgerowl/"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-cool-500 hover:text-blue-primary-500 transition-colors"
    >
      <svg width={18} height={18} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M10.315 0C12.745 0 13.099 0.013 14.123 0.06C15.187 0.109 15.914 0.278 16.55 0.525C17.218 0.776 17.822 1.169 18.322 1.678C18.831 2.178 19.224 2.783 19.475 3.45C19.722 4.086 19.891 4.813 19.94 5.877C19.988 6.944 20 7.284 20 10V10.08C20 12.723 19.988 13.067 19.94 14.123C19.891 15.187 19.722 15.914 19.475 16.55C19.224 17.218 18.831 17.822 18.322 18.322C17.822 18.831 17.218 19.224 16.55 19.475C15.914 19.722 15.187 19.891 14.123 19.94C13.056 19.988 12.716 20 10 20H9.92C7.277 20 6.933 19.988 5.877 19.94C4.813 19.891 4.086 19.722 3.45 19.475C2.783 19.224 2.178 18.831 1.678 18.322C1.169 17.822 0.776 17.218 0.525 16.55C0.278 15.914 0.109 15.187 0.06 14.123C0.013 13.099 0 12.744 0 10.315V9.685C0 7.255 0.013 6.901 0.06 5.877C0.109 4.813 0.278 4.086 0.525 3.45C0.776 2.783 1.169 2.178 1.678 1.678C2.178 1.169 2.783 0.776 3.45 0.525C4.086 0.278 4.813 0.109 5.877 0.06C6.901 0.013 7.256 0 9.685 0H10.315ZM10 6.667C8.159 6.667 6.667 8.159 6.667 10C6.667 11.841 8.159 13.333 10 13.333C11.841 13.333 13.333 11.841 13.333 10C13.333 8.159 11.841 6.667 10 6.667ZM10 4.865C12.837 4.865 15.135 7.163 15.135 10C15.135 12.837 12.837 15.135 10 15.135C7.163 15.135 4.865 12.837 4.865 10C4.865 7.163 7.163 4.865 10 4.865Z" fill="#9CA3AF" />
      </svg>
    </Link>
  </div>
);

const Footer = () => {
  return (
    <Fragment>
      {/* Desktop Footer */}
      <footer className="py-20 px-6 lg:px-10 bg-cream border-t border-black/[0.06]">
        <div className="max-w-[1340px] mx-auto">
          {/* Main grid: brand + 2 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.3fr_repeat(2,1fr)] gap-12 lg:gap-12">
            {/* Brand column */}
            <div>
              <div className="mb-4">
                <Logo className="h-6 w-auto" />
              </div>
              <div className="font-inter text-[14px] text-gray-cool-500 leading-[1.8]">
                <p className="mb-3">
                  <span role="img" aria-label="Australian flag">🇦🇺</span> Ledgerowl Pty Ltd
                  <br />
                  <strong className="text-gray-cool-700">Greenhouse Climate Tech Hub</strong>{" "}
                  (Investible)
                  <br />
                  Level 3, Salesforce Tower, 180 George St,
                  <br />
                  Sydney NSW 2000, Australia
                </p>
                <p className="mb-3">
                  <span role="img" aria-label="Singapore flag">🇸🇬</span> Ledgerowl Pte Ltd
                  <br />
                  160 Robinson Road, #14-04,
                  <br />
                  Singapore 068914
                </p>
                <p>&copy; 2026 All Rights Reserved</p>
              </div>
            </div>

            {/* Link columns */}
            {footerColumns.map((col) => (
              <div key={col.title}>
                <h5 className="font-inter text-[14px] font-bold text-gray-cool-900 uppercase tracking-[1px] mb-[18px]">
                  {col.title}
                </h5>
                {col.links.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    className="block font-inter text-[14px] text-gray-cool-500 hover:text-blue-primary-500 transition-colors mb-3"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="mt-10 pt-6 border-t border-black/[0.06] flex flex-col sm:flex-row justify-between items-center gap-4">
            <span className="font-inter text-[13px] text-gray-cool-500 text-center sm:text-left">
              AI-powered 🤖 · Human-verified 👩‍💼 · Built for Australian
              businesses 🇦🇺
            </span>
            <SocialLinks />
          </div>

          {/* Legal disclaimer */}
          <div className="mt-6 text-center">
            <p className="font-inter text-[11px] text-gray-cool-400 max-w-3xl mx-auto">
              Ledgerowl provides AI-powered accounting and finance operations
              services to help businesses reduce costs and improve accuracy.
              Ledgerowl is not a registered public accounting firm. Any
              information provided does not constitute financial advice. Please
              seek professional guidance for your specific situation.
            </p>
          </div>
        </div>
      </footer>
    </Fragment>
  );
};

export default Footer;
